import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGetUiPrefs, apiSetUiPref, getTenantSlug } from "@/lib/api";

/**
 * Persists a boolean UI preference in the database.
 *
 * Returns [value, setValue] where `value` is `undefined` while the initial
 * fetch is in flight (so callers can avoid rendering content until the real
 * state is known, preventing open→close flicker on page load).
 */
export function useUiPref(
  key: string,
  defaultValue: boolean,
  options: { enabled?: boolean } = {},
): [boolean | undefined, (v: boolean) => void] {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const [value, setValue] = useState<boolean | undefined>(undefined);
  const reconciled = useRef(false);

  const { data: prefs, isFetched } = useQuery({
    queryKey: ["ui-prefs", getTenantSlug()],
    queryFn: apiGetUiPrefs,
    staleTime: Infinity,
    enabled,
  });

  useEffect(() => {
    if (!isFetched || reconciled.current) return;
    reconciled.current = true;
    const stored = prefs && key in prefs ? prefs[key] === "true" : defaultValue;
    console.log(`[useUiPref] loaded key="${key}" raw="${prefs?.[key]}" → ${stored}`);
    setValue(stored);
  }, [isFetched, prefs, key, defaultValue]);

  const mutation = useMutation({
    mutationFn: (v: boolean) => {
      console.log(`[useUiPref] saving key="${key}" value=${v}`);
      return apiSetUiPref(key, String(v));
    },
    onSuccess: (_data, v) => {
      console.log(`[useUiPref] saved key="${key}" value=${v}`);
      queryClient.setQueryData<Record<string, string>>(
        ["ui-prefs", getTenantSlug()],
        (old) => ({ ...old, [key]: String(v) }),
      );
    },
    onError: (err) => {
      console.error(`[useUiPref] save failed key="${key}":`, err);
    },
  });

  const set = (v: boolean) => {
    setValue(v);
    mutation.mutate(v);
  };

  return [value, set];
}
